import React from 'react';
import { Button, Container, Row, Col, Form } from 'react-bootstrap';
import { useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSave } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal'; // <-- make sure path matches your project
import InformationModal from '../../Components/InformationModal/InformationModal';
import { NitAccountMapApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import ServerTable from '../../Components/Table/ServerTabe';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';

const NitGlMapPageContent = () => {
    const { instance, accounts } = useMsal();

    // --- Company selector state ---
    const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
    const [companyOptions, setCompanyOptions] = React.useState([]); // [{value,label,raw}]
    const [loadingCompanies, setLoadingCompanies] = React.useState(false);

    // GL accounts
    const [glAccounts, setGlAccounts] = React.useState([]);
    const [loadingAccounts, setLoadingAccounts] = React.useState(false);
    const glAccountOptions = React.useMemo(
        () => glAccounts.map(a => ({ value: a.code, label: `${a.formatCode} — ${a.name ?? ''}` })),
        [glAccounts]
    );
    const findOptionByCode = React.useCallback(
        (code) => (code ? glAccountOptions.find(o => o.value === code) ?? null : null),
        [glAccountOptions]
    );

    // Caches
    // current values user is editing
    const [mapCache, setMapCache] = React.useState({}); // {[nit]: { id, glAccountCode, isActive }}
    // server baseline to detect changes (per NIT)
    const [baseMapCache, setBaseMapCache] = React.useState({});
    // track which NITs are dirty (edited vs baseline)
    const [dirtyNits, setDirtyNits] = React.useState(new Set());
    // last page rows to show preview in modal
    const [lastPageRows, setLastPageRows] = React.useState([]);

    // Toolbar state
    const [refreshTick, setRefreshTick] = React.useState(0);
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [savingAll, setSavingAll] = React.useState(false);
    const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });

    const showInfoModal = React.useCallback((title, content) => {
        setInfoModal({ show: true, title, content });
    }, []);

    const hideInfoModal = React.useCallback(() => {
        setInfoModal(prev => ({ ...prev, show: false }));
    }, []);

    const acquireToken = React.useCallback(async () => {
        const req = { ...loginRequest, account: accounts[0] };
        const { accessToken } = await instance.acquireTokenSilent(req);
        return accessToken;
    }, [instance, accounts]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoadingCompanies(true);
                const token = await acquireToken();
                const arr = await SapMasterApi.getCompanies(token); // [{companyId, companyDb}]
                if (cancelled) return;
                const opts = (arr ?? []).map(c => ({
                    value: c.companyId,
                    label: c.companyName ?? `Company ${c.companyId}`,
                    raw: c
                }));
                setCompanyOptions(opts);
                if (companyId && !opts.some((o) => o.value === companyId)) {
                    setCompanyId(null);
                    setStoredConciliationCompanyId(null);
                }
            } catch (err) {
                if (!cancelled) console.error('Failed to load companies', err);
            } finally {
                if (!cancelled) setLoadingCompanies(false);
            }
        })();
        return () => { cancelled = true; };
    }, [acquireToken, companyId]); // load once

    // When company changes, clear caches and refresh
    const handleCompanyChange = (opt) => {
        const id = opt?.value ?? null;
        setCompanyId(id);
        setStoredConciliationCompanyId(id);
        // reset page caches (so we don’t mix companies)
        setBaseMapCache({});
        setMapCache({});
        setDirtyNits(new Set());
        setLastPageRows([]);
        // allow masters to re-load for this new company if not loaded before
        // (we keep the Set so masters load just once per company)
        setRefreshTick(t => t + 1); // force table refresh
    };

    // Load GL accounts once per company (idempotent)
    const loadedAccountsForCompanyRef = React.useRef(new Set());
    React.useEffect(() => {
        if (!companyId) return;
        if (loadedAccountsForCompanyRef.current.has(companyId)) return;
        let cancelled = false;
        (async () => {
            try {
                setLoadingAccounts(true);
                const token = await acquireToken();
                const res = await SapMasterApi.getGlAccounts(
                    companyId, { search: '', page: 1, pageSize: 2000 }, token
                );
                if (cancelled) return;
                setGlAccounts((res.items ?? []).filter(a => a.cardType = 'S').map(a => ({ code: a.code, name: a.name, formatCode: a.formatCode })));
                loadedAccountsForCompanyRef.current.add(companyId);
            } catch (err) {
                if (!cancelled) console.error('Failed to load GL accounts', err);
            } finally {
                if (!cancelled) setLoadingAccounts(false);
            }
        })();
        return () => { cancelled = true; };
    }, [companyId, acquireToken]);

    // Helper: check/flip dirty flag for a NIT based on diff vs baseline
    const reconcileDirty = React.useCallback((nit, nextValueObj) => {
        const base = baseMapCache[nit] || { glAccountCode: null, isActive: true };
        const isDirty =
            (base.glAccountCode ?? null) !== (nextValueObj.glAccountCode ?? null) ||
            (base.isActive ?? true) !== (nextValueObj.isActive ?? true);

        setDirtyNits(prev => {
            const clone = new Set(prev);
            if (isDirty) clone.add(nit); else clone.delete(nit);
            return clone;
        });
    }, [baseMapCache]);

    // Data fetch for the ServerTable (server already LEFT JOINs mapping)
    const fetchData = React.useCallback(async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => {
        if (!companyId) return { items: [], totalItems: 0 };
        const token = await acquireToken();

        let search = '';
        if (globalFilter) search = globalFilter;
        columnFilters?.forEach(f => {
            if (!f?.value) return;
            const id = f.id?.toLowerCase();
            if (id === 'nit' || id === 'cardcode' || id === 'cardname') {
                search = search ? `${search} ${f.value}` : f.value;
            }
        });

        const res = await SapMasterApi.getBusinessPartnersWithNit(
            companyId,
            { search, page: pageIndex + 1, pageSize },
            token
        );

        var rows = res.items ?? [];

        // seed both baseline and current cache for the rows on this page
        const seededBase = {};
        const seededCurrent = {};
        rows = rows.filter(x => x.cardType == 'cSupplier');
        rows.forEach(r => {
            const nit = r.nit;
            const cur = {
                id: r.mappingId ?? null,
                glAccountCode: r.mappedGlAccountCode ?? null,
                isActive: (r.mappingIsActive ?? true)
            };
            seededBase[nit] = cur;
            // for current, prefer what the user may have already edited in mapCache
            seededCurrent[nit] = mapCache[nit] ?? cur;
        });

        setBaseMapCache(prev => ({ ...prev, ...seededBase }));
        setMapCache(prev => ({ ...seededCurrent, ...prev })); // ensure existing edits win
        setLastPageRows(rows);

        // recompute dirty for page nits (in case of refresh)
        const pageNits = new Set(rows.map(r => r.nit));
        setDirtyNits(prev => {
            const next = new Set(prev);
            for (const nit of pageNits) {
                const base = seededBase[nit];
                const cur = seededCurrent[nit] ?? base;
                const isDirty =
                    (base.glAccountCode ?? null) !== (cur.glAccountCode ?? null) ||
                    (base.isActive ?? true) !== (cur.isActive ?? true);
                if (isDirty) next.add(nit); else next.delete(nit);
            }
            return next;
        });

        return {
            items: rows,
            totalItems: res.totalItems ?? 0
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, acquireToken, refreshTick, /* DO NOT add mapCache here to avoid loops */]);

    // Change handlers mark dirty
    const updateGlForNit = React.useCallback((nit, codeOrNull) => {
        setMapCache(prev => {
            const cur = prev[nit] ?? { id: null, glAccountCode: null, isActive: true };
            const next = { ...cur, glAccountCode: codeOrNull };
            // reconcile dirty after state change
            setTimeout(() => reconcileDirty(nit, next), 0);
            return { ...prev, [nit]: next };
        });
    }, [reconcileDirty]);

    const updateActiveForNit = React.useCallback((nit, isActive) => {
        setMapCache(prev => {
            const cur = prev[nit] ?? { id: null, glAccountCode: null, isActive: true };
            const next = { ...cur, isActive };
            setTimeout(() => reconcileDirty(nit, next), 0);
            return { ...prev, [nit]: next };
        });
    }, [reconcileDirty]);

    // Save All flow
    const openConfirm = React.useCallback(() => {
        if (dirtyNits.size === 0) return;
        setShowConfirm(true);
    }, [dirtyNits]);

    const handleConfirmSaveAll = React.useCallback(async () => {
        setSavingAll(true);
        try {
            const token = await acquireToken();

            const dirtyList = Array.from(dirtyNits);
            // Save only for NITs that have a selected account
            const payloads = dirtyList
                .map(nit => ({ nit, ...mapCache[nit] }))
                .filter(x => !!x.glAccountCode);

            // POST all (sequential to keep server cooler; switch to Promise.all if you prefer)
            for (const p of payloads) {
                await NitAccountMapApi.upsertNitAccountMap(
                    companyId,
                    {
                        id: p.id,           // null for insert
                        nIT: p.nit,
                        gLAccountCode: p.glAccountCode,
                        isActive: p.isActive ?? true
                    },
                    token
                );
            }

            // After success: promote current -> baseline for saved NITs, clear dirty flags
            setBaseMapCache(prev => {
                const clone = { ...prev };
                for (const p of payloads) clone[p.nit] = { id: p.id, glAccountCode: p.glAccountCode, isActive: p.isActive };
                return clone;
            });
            setDirtyNits(prev => {
                const next = new Set(prev);
                for (const p of payloads) next.delete(p.nit);
                return next;
            });

            const skipped = dirtyList.length - payloads.length;
            setShowConfirm(false);
            showInfoModal(
                'Mappings saved',
                <div>
                    <p className="mb-0"><strong>{payloads.length}</strong> mapping(s) saved successfully.</p>
                    {skipped > 0 && (
                        <p className="text-muted small mb-0">{skipped} change(s) skipped because a GL account was not selected.</p>
                    )}
                </div>
            );
        } catch (err) {
            console.error('Save all failed', err);
            setShowConfirm(false);
            showInfoModal(
                'Save failed',
                <p className="mb-0">Some mappings could not be saved. Please try again.</p>
            );
        } finally {
            setSavingAll(false);
        }
    }, [dirtyNits, mapCache, companyId, acquireToken, showInfoModal]);

    // Columns
    const columns = React.useMemo(() => [
        { header: 'NIT', accessorKey: 'nit', id: 'NIT' },
        { header: 'Card Code', accessorKey: 'cardCode', id: 'CardCode' },
        { header: 'Card Name', accessorKey: 'cardName', id: 'CardName' },
        {
            header: 'GL Account',
            id: 'glAccount',
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({ row }) => {
                const nit = row.original.nit;
                const cached = mapCache[nit] || { id: null, glAccountCode: '', isActive: true };
                return (
                    <Select
                        classNamePrefix="react-select"
                        isDisabled={loadingAccounts}
                        options={glAccountOptions}
                        value={findOptionByCode(cached.glAccountCode)}
                        onChange={(opt) => updateGlForNit(nit, opt?.value ?? null)}
                        isClearable
                        placeholder="Select account…"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                );
            }
        },
        // {
        //   header: 'Active',
        //   id: 'active',
        //   enableSorting: false,
        //   enableColumnFilter: false,
        //   cell: ({ row }) => {
        //     const nit = row.original.nit;
        //     const cached = mapCache[nit] || { id: null, glAccountCode: null, isActive: true };
        //     return (
        //       <Form.Check
        //         type="switch"
        //         checked={!!cached.isActive}
        //         onChange={(e) => updateActiveForNit(nit, e.target.checked)}
        //       />
        //     );
        //   }
        // }
        // NOTE: no per-row Save button anymore; we use Save All in the toolbar
    ], [glAccountOptions, mapCache, loadingAccounts, findOptionByCode, updateGlForNit, updateActiveForNit]);

    // Build preview rows for the confirmation modal (only dirty, and only those with a selected GL)
    const previewItems = React.useMemo(() => {
        const list = [];
        const dirtyList = Array.from(dirtyNits);
        for (const nit of dirtyList) {
            const base = baseMapCache[nit] ?? { glAccountCode: null, isActive: true };
            const cur = mapCache[nit] ?? base;
            if (!cur.glAccountCode) continue; // skip not-selected
            const row = lastPageRows.find(r => r.nit === nit); // page rows (CardName, etc.)
            list.push({
                nit,
                cardCode: row?.cardCode ?? '',
                cardName: row?.cardName ?? '',
                from: base.glAccountCode ?? '(none)',
                to: cur.glAccountCode,
                active: cur.isActive ? 'Yes' : 'No'
            });
        }
        return list;
    }, [dirtyNits, baseMapCache, mapCache, lastPageRows]);

    // Find selected company option
    const selectedCompany = React.useMemo(
        () => companyOptions.find(o => o.value === companyId) ?? null,
        [companyOptions, companyId]
    );

    return (
        <Container fluid className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <div style={{ minWidth: 280 }}>
                        <Select
                            classNamePrefix="react-select"
                            options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
                            value={selectedCompany}
                            onChange={handleCompanyChange}
                            isLoading={loadingCompanies}
                            placeholder="Select company…"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                        />
                    </div>
                </div>

                <div className="d-flex">
                    <button
                        className="btn btn-xl btn-outline-primary fw-bold me-2"
                        onClick={() => setRefreshTick(t => t + 1)}
                        disabled={!companyId}
                        title="Refresh"
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                    </button>

                    <button
                        className="btn btn-xl btn-primary fw-bold"
                        onClick={() => openConfirm()}
                        disabled={dirtyNits.size === 0 || !companyId}
                        title={dirtyNits.size ? `Save ${dirtyNits.size} change(s)` : 'No changes to save'}
                    >
                        <FontAwesomeIcon icon={faSave} className="me-1" />
                        Save All
                    </button>
                </div>
            </div>

            <Row className="my-3">
                <Col sm={12}>
                    {!companyId && (
                        <div className="alert alert-info mb-3">
                            Select a company to load NIT to GL mapping records.
                        </div>
                    )}
                    <ServerTable
                        columns={columns}
                        fetchData={fetchData}
                        tableClass="table-hover"
                        containerClass="table-responsive"
                        headerClass="table-light"
                        enableGlobalFilter={false}
                        initialPageSize={10}
                    />
                </Col>
            </Row>

            {/* Confirmation modal */}
            <ConfirmationModal
                show={showConfirm}
                onHide={() => setShowConfirm(false)}
                title="Confirm mappings to save"
                content={
                    <div>
                        <p className="mb-2">
                            You are about to save <strong>{previewItems.length}</strong> mapping(s).
                        </p>
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>NIT</th>
                                        <th>Card Code</th>
                                        <th>Card Name</th>
                                        <th>GL (current)</th>
                                        <th>GL (new)</th>
                                        <th>Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewItems.map((it) => (
                                        <tr key={it.nit}>
                                            <td>{it.nit}</td>
                                            <td>{it.cardCode}</td>
                                            <td>{it.cardName}</td>
                                            <td>{it.from}</td>
                                            <td>{it.to}</td>
                                            <td>{it.active}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {previewItems.length === 0 && (
                            <div className="text-muted small mt-2">No valid changes to save (select a GL account to proceed).</div>
                        )}
                    </div>
                }
                onAcceptClick={handleConfirmSaveAll}
                onCancelClick={() => setShowConfirm(false)}
            />
            <InformationModal
                show={infoModal.show}
                onHide={hideInfoModal}
                onAcceptClick={hideInfoModal}
                title={infoModal.title}
                content={infoModal.content}
            />
        </Container>
    );
};

const NitGlMapPage = () => (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <PageLayout2 title="NIT → GL Mappings (from SAP BPs)">
            <NitGlMapPageContent />
        </PageLayout2>
    </MsalAuthenticationTemplate>
);

export default NitGlMapPage;
